import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Transaction } from '@/types';

type EditTransactionDialogProps = {
  transaction: Transaction | null;
  open: boolean;
  onClose: () => void;
  onSave: (id: number, type: 'deposit' | 'withdrawal', amount: number, date: Date) => Promise<void>;
};

export const EditTransactionDialog = ({ transaction, open, onClose, onSave }: EditTransactionDialogProps): React.ReactNode => {
  const [type, setType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [amount, setAmount] = useState(0);
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (transaction) {
      const transactionType = transaction.transaction_type || transaction.type;
      setType(transactionType === 'withdrawal' ? 'withdrawal' : 'deposit');
      setAmount(Math.abs(transaction.amount));

      const transactionDate = transaction.transaction_time
        ? new Date(transaction.transaction_time * 1000)
        : new Date(transaction.date!);
      setDate(transactionDate.toISOString().split('T')[0]);
    }
  }, [transaction]);

  const handleSave = async () => {
    if (!transaction?.id) return;

    setSaving(true);
    try {
      await onSave(transaction.id, type, amount, new Date(date));
      onClose();
    } catch (error) {
      console.error('Failed to update transaction:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Transaction</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="edit-date">Date</Label>
            <Input
              id="edit-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-type">Type</Label>
            <Select value={type} onValueChange={(value: 'deposit' | 'withdrawal') => setType(value)}>
              <SelectTrigger id="edit-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="deposit">Deposit</SelectItem>
                <SelectItem value="withdrawal">Withdrawal</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-amount">Amount</Label>
            <Input
              id="edit-amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
